import { getApiUrl } from '@/lib/api-config';
import { apiUpload } from '@/lib/api-request';
import { MAX_FILE_BYTES } from '@/lib/file-upload';
import { deleteEntityAttachment } from '@/lib/attachment-delete';
import type { GenericAttachment } from '@/styles/GenericAttachmentsSection';
import { driveItemIdOf, isDriveFileRef } from '@/lib/drive-file-ref';
import { documentUrl } from '@/lib/file-urls';

/**
 * The ONE frontend implementation of attachments for every record type
 * (except Drive): upload, list and delete against the unified `attachments`
 * table and the single private `attachments` Storage bucket
 * (backend: modules/attachments, phase103_unified_attachments.sql).
 * Modules call this with just an entity type + the record's id -- no module
 * knows a table, a bucket or a column. The attachment count of a list page
 * is computed by the backend (`include=counts`, core/entity-counts.js).
 *
 * The browser never touches Storage and never decides ownership: it sends the
 * file to the backend (multipart POST /api/entities/attachment) and the server
 * validates the parent record and the caller's permission, stores the bytes at
 * {companyId}/{entityType}/{entityId}/{random name} and derives
 * company/project/author itself -- removing the object again if the record is
 * refused.
 */
export const ATTACHMENTS_BUCKET = 'attachments';

/** Mirrors the backend allow-list (modules/attachments/entity-map.js) and the DB table attachment_entity_types. */
export type AttachmentEntityType =
  | 'bid' | 'bid_package' | 'bill' | 'bill_payment' | 'change_order' | 'checklist' | 'daily_log'
  | 'invoice' | 'invoice_payment' | 'material_list' | 'project' | 'proposal'
  | 'purchase_order' | 'schedule_task' | 'selection_option' | 'specification'
  | 'task' | 'user' | 'warranty';

/** DB row -> what the UI renders. */
export function toGenericAttachment(row: Record<string, any>, fileUrl: string, uploader?: { display_name?: string | null } | null): GenericAttachment {
  return {
    id: row.id,
    file_name: row.file_name,
    file_type: row.file_type,
    file_size: typeof row.file_size === 'number' ? row.file_size : Number(row.file_size ?? 0),
    file_url: fileUrl,
    created_at: row.created_at,
    created_by: row.created_by,
    created_by_user: uploader ?? row.created_by_user ?? undefined,
    metadata: row.metadata ?? undefined,
  };
}

export interface FetchAttachmentsOptions {
  ascending?: boolean;
  /** Only files whose metadata.document_type matches (user records). */
  documentType?: string;
  /** true: only expiring documents; false: only files without an expiry. */
  hasExpiry?: boolean;
}

/**
 * The live attachments of one or many records, read through the API
 * (GET /api/entities/attachment): the server applies the caller's RLS and joins the
 * uploader's name; no Storage URL is involved.
 */
async function fetchAttachmentRows(
  entityType: AttachmentEntityType,
  entityIds: string[],
  opts: FetchAttachmentsOptions
): Promise<any[]> {
  const params = new URLSearchParams({ entity_type: entityType, entity_id: entityIds.join(',') });
  if (opts.ascending === false) params.set('order', 'desc');
  if (opts.documentType) params.set('document_type', opts.documentType);
  if (opts.hasExpiry !== undefined) params.set('has_expiry', String(opts.hasExpiry));
  const response = await fetch(getApiUrl(`api/entities/attachment?${params}`));
  if (!response.ok) throw new Error(`status ${response.status}`);
  const body = await response.json();
  return body.data ?? [];
}

// `file_url` of an attachment is its permanent KrewUp address (opens the viewer page, which asks the backend again on every visit); the bytes of a picture are loaded through the backend (ProtectedImage)
const rowToAttachment = (row: any): GenericAttachment => toGenericAttachment(row, documentUrl('attachment', row.id));

/**
 * A record's live attachments as the raw API rows (file name / type AND `storage_path`), for the rare caller that has to
 * hand a file's Storage object path back to the backend (e.g. to have a PDF read server-side). The backend verifies the
 * path belongs to the caller's company before using it.
 */
export async function fetchEntityAttachmentFiles(
  entityType: AttachmentEntityType,
  entityId: string
): Promise<{ id: string; file_name: string; file_type: string; storage_path: string }[]> {
  const rows = await fetchAttachmentRows(entityType, [entityId], { ascending: false });
  return rows.map((row) => ({ id: row.id, file_name: row.file_name, file_type: row.file_type, storage_path: row.storage_path }));
}

/** A record's live attachments (not soft-deleted), with uploader names and their permanent KrewUp addresses. */
export async function fetchEntityAttachments(
  entityType: AttachmentEntityType,
  entityId: string,
  opts: FetchAttachmentsOptions = {}
): Promise<GenericAttachment[]> {
  try {
    return (await fetchAttachmentRows(entityType, [entityId], opts)).map(rowToAttachment);
  } catch (error) {
    console.error(`[attachments:${entityType}] failed to fetch attachments`, error);
    return [];
  }
}

/** Live attachments of MANY records at once (list pages), grouped by record id. */
export async function fetchEntityAttachmentsByIds(
  entityType: AttachmentEntityType,
  entityIds: string[]
): Promise<Map<string, GenericAttachment[]>> {
  const map = new Map<string, GenericAttachment[]>();
  if (entityIds.length === 0) return map;
  try {
    for (const row of await fetchAttachmentRows(entityType, entityIds, { ascending: true })) {
      const list = map.get(row.entity_id) ?? [];
      list.push(rowToAttachment(row));
      map.set(row.entity_id, list);
    }
  } catch (error) {
    console.error(`[attachments:${entityType}] failed to fetch attachments`, error);
  }
  return map;
}

/** The {name,type,url,size,uploadedBy,createdAt} shape Daily Logs and Warranties render. */
export interface NamedAttachment {
  id: string;
  name: string;
  type: string;
  url: string;
  size?: number;
  uploadedBy?: string;
  createdAt?: string;
}
export function toNamedAttachment(att: GenericAttachment): NamedAttachment {
  return {
    id: att.id,
    name: att.file_name,
    type: att.file_type,
    url: att.file_url,
    size: att.file_size,
    uploadedBy: att.created_by ?? undefined,
    createdAt: att.created_at ?? undefined,
  };
}

/**
 * Why files were not attached, collected for whoever shows the result: a caller that passes `onFileError` gets each reason
 * itself; otherwise the reasons wait here until the attachments section (or staging hook) that started the upload takes them.
 */
let pendingFailures: string[] = [];
export function takeAttachmentFailures(): string[] {
  const failures = pendingFailures;
  pendingFailures = [];
  return failures;
}
function reportFileError(onFileError: ((fileName: string, reason: string) => void) | undefined, fileName: string, reason: string) {
  if (onFileError) onFileError(fileName, reason);
  else pendingFailures.push(`"${fileName}" was not attached: ${reason}`);
}

/** POST /api/entities/attachment/from-drive: the record's own attachments from files of its project's Drive. */
async function attachDriveFiles(
  entityType: AttachmentEntityType,
  entityId: string,
  files: File[],
  onFileError?: (fileName: string, reason: string) => void
): Promise<GenericAttachment[]> {
  const byId = new Map(files.map((file) => [driveItemIdOf(file as any), file.name]));
  try {
    const response = await fetch(getApiUrl('api/entities/attachment/from-drive'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entity_type: entityType, entity_id: entityId, drive_item_ids: Array.from(byId.keys()) }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body?.error ?? `the server answered ${response.status}`);
    for (const failure of body.data?.failed ?? []) reportFileError(onFileError, byId.get(failure.id) ?? failure.name, failure.reason);
    return (body.data?.attachments ?? []).map(rowToAttachment);
  } catch (err) {
    console.error(`[attachments:${entityType}] attaching from the Drive failed`, err);
    const reason = err instanceof Error ? err.message : 'the Drive files could not be attached';
    Array.from(byId.values()).forEach((name) => reportFileError(onFileError, name, reason));
    return [];
  }
}

export async function uploadEntityAttachments(
  entityType: AttachmentEntityType,
  entityId: string | undefined,
  files: File[],
  companyId: string,
  userId: string,
  opts: {
    metadata?: Record<string, unknown>;
    /** Why a file was NOT uploaded (the server's own message), so the caller can tell the user instead of only logging it. */
    onFileError?: (fileName: string, reason: string) => void;
  } = {}
): Promise<GenericAttachment[]> {
  const label = `attachments:${entityType}`;
  if (!entityId) {
    console.error(`[${label}] upload: missing record id`);
    return [];
  }
  if (!companyId || !userId) {
    console.error(`[${label}] upload: missing companyId or userId`);
    return [];
  }
  if (!files || files.length === 0) return [];

  const uploaded: GenericAttachment[] = [];

  // Files picked from the project's Drive ("Browse Drive") are not uploaded: the server copies them inside Storage, in ONE request.
  const driveFiles = files.filter(isDriveFileRef);
  if (driveFiles.length > 0) {
    uploaded.push(...(await attachDriveFiles(entityType, entityId, driveFiles, opts.onFileError)));
    files = files.filter((file) => !isDriveFileRef(file));
  }

  for (const file of files) {
    try {
      if (!(file instanceof File) || !file.size || file.size > MAX_FILE_BYTES) {
        console.error(`[${label}] skipping invalid/empty/oversized file`, file?.name);
        reportFileError(opts.onFileError, file?.name ?? 'file', !file?.size ? 'the file is empty' : `the file is larger than ${Math.round(MAX_FILE_BYTES / (1024 * 1024))} MB`);
        continue;
      }

      const form = new FormData();
      form.append('entity_type', entityType);
      form.append('entity_id', entityId);
      form.append('file_name', file.name);
      if (opts.metadata) form.append('metadata', JSON.stringify(opts.metadata));
      form.append('file', file, file.name);
      // The API returns the saved row WITH the uploader; its address is the permanent KrewUp one.
      uploaded.push(rowToAttachment(await apiUpload<Record<string, any>>('api/entities/attachment', form)));
    } catch (err) {
      console.error(`[${label}] upload failed`, err);
      reportFileError(opts.onFileError, file?.name ?? 'file', err instanceof Error ? err.message : 'the file could not be attached');
    }
  }
  return uploaded;
}

/** Deletes one attachment (the server soft-deletes the row and removes the object). */
export function deleteAttachment(attachmentId: string, userId: string | undefined): Promise<boolean> {
  return deleteEntityAttachment({ attachmentId, userId });
}

/** Renames an attachment and/or edits its allow-listed metadata (e.g. a document's expiry). */
export async function updateAttachment(
  attachmentId: string,
  patch: { file_name?: string; metadata?: Record<string, unknown> }
): Promise<boolean> {
  try {
    const response = await fetch(getApiUrl(`api/entities/attachment/${attachmentId}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!response.ok) console.error('[attachments] update failed', response.status);
    return response.ok;
  } catch (err) {
    console.error('[attachments] update failed', err);
    return false;
  }
}
